/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  CategoryCard,
  CategoryHeader,
  CategoryTitle,
  ColorSummary,
  ColorSwatch,
  ContactGroupCard,
  ContactMethodRow,
  EmptyState,
  ExclusionCategory,
  ExclusionGroup,
  ExclusionItem,
  GlobalDetailsContainer,
  Row,
  RowHeader,
  RowHeaderText,
  RowValue,
  SectionBody,
  SectionCard,
  SectionHeader,
  SectionHeaderTitle,
  SectionTitle,
  ValueText,
} from "./styles";
import { groupExclusionItems } from "../discount_card_exclusions/utils";
import { SPECIAL_PERQ_GROUPS_I18N_NAMESPACE } from "../../pages/discount_cards/shared/specialPerqGroupsI18n";
import { CARD_TYPE_INDUSTRY } from "../../pages/discount_cards/shared/audienceGroups";

const specialPerqGroupsKey = (key) =>
  `${SPECIAL_PERQ_GROUPS_I18N_NAMESPACE}.${key}`;

const normalizeValue = (value, fallback = "-") => {
  const text = value?.toString?.().trim?.() || "";
  return text.length > 0 ? text : fallback;
};

const normalizeItems = (items = []) =>
  Array.isArray(items)
    ? items.filter(
        (item) =>
          item?.phone_number?.trim() ||
          item?.method_type ||
          item?.label?.trim(),
      )
    : [];

const renderCategories = (rules, t) => {
  if (!Array.isArray(rules) || rules.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {rules.map((rule, ruleIndex) => {
        const perks = Array.isArray(rule?.perks) ? rule.perks : [];

        return (
          <CategoryCard key={rule?.category_name ?? ruleIndex}>
            <CategoryHeader>
              <CategoryTitle>
                {normalizeValue(
                  rule?.category_name || rule?.category,
                  t("discount-cards.create.form.rules.property"),
                )}
              </CategoryTitle>
            </CategoryHeader>
            <RowHeader>
              <RowHeaderText>
                {t("discount-cards.create.form.rules.people")}
              </RowHeaderText>
              <RowHeaderText>
                {t("discount-cards.create.form.rules.discount-section")}
              </RowHeaderText>
              <RowHeaderText>
                {t("discount-cards.create.form.rules.applicability")}
              </RowHeaderText>
            </RowHeader>
            {perks.length > 0 ? (
              perks.map((perk, perkIndex) => (
                <Row key={perk?.id ?? `${ruleIndex}-${perkIndex}`}>
                  <RowValue>{normalizeValue(perk?.label)}</RowValue>
                  <RowValue>{normalizeValue(perk?.discount)}</RowValue>
                  <RowValue>{normalizeValue(perk?.type)}</RowValue>
                </Row>
              ))
            ) : (
              <Box sx={{ p: 2 }}>
                <EmptyState>
                  {t(specialPerqGroupsKey("empty-categories"))}
                </EmptyState>
              </Box>
            )}
          </CategoryCard>
        );
      })}
    </Box>
  );
};

const renderContactMethods = (contactMethodsByType, t) => {
  const sections = [
    {
      key: "voice",
      title: t("discount-cards.create.form.contact-methods.sections.voice"),
      items: normalizeItems(contactMethodsByType?.voice),
    },
    {
      key: "message",
      title: t("discount-cards.create.form.contact-methods.sections.message"),
      items: normalizeItems(contactMethodsByType?.message),
    },
  ].filter((section) => section.items.length > 0);

  if (sections.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {sections.map((section) => (
        <ContactGroupCard key={section.key}>
          <SectionHeader>
            <SectionHeaderTitle>{section.title}</SectionHeaderTitle>
          </SectionHeader>
          {section.items.map((contactMethod, index) => (
            <ContactMethodRow
              key={contactMethod?.id ?? `${section.key}-${index}`}
            >
              <ValueText>
                {normalizeValue(contactMethod?.phone_number, "-")}
              </ValueText>
              <ValueText>
                {normalizeValue(
                  t(
                    `discount-cards.create.form.contact-methods.options.${contactMethod?.method_type}`,
                    { defaultValue: contactMethod?.method_type },
                  ),
                )}
              </ValueText>
              <ValueText>{normalizeValue(contactMethod?.label)}</ValueText>
            </ContactMethodRow>
          ))}
        </ContactGroupCard>
      ))}
    </Box>
  );
};

const renderRestaurantScope = (discountCard, restaurantGroupName, t) => {
  const isSpecific = discountCard?.restaurant_scope_type === "specific_restaurants";
  const scopedRestaurants = Array.isArray(discountCard?.restaurants)
    ? discountCard.restaurants
    : [];

  if (isSpecific) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        <ValueText sx={{ fontWeight: 600 }}>
          {t("discount-cards.index.discount-modal.restaurant-usage-specific")}
        </ValueText>
        {scopedRestaurants.length > 0 ? (
          scopedRestaurants.map((restaurant, index) => (
            <ValueText
              key={restaurant?.id ?? index}
              data-testid="discount-card-global-details-restaurant-scope-item"
            >
              {normalizeValue(restaurant?.name)}
            </ValueText>
          ))
        ) : (
          <EmptyState>
            {t("discount-cards.index.discount-modal.restaurant-usage-empty")}
          </EmptyState>
        )}
      </Box>
    );
  }

  return (
    <ValueText>
      {restaurantGroupName?.toString?.().trim?.()
        ? t("discount-cards.index.discount-modal.restaurant-usage-all-named", {
            group: restaurantGroupName,
          })
        : t("discount-cards.index.discount-modal.restaurant-usage-all")}
    </ValueText>
  );
};

// Issue-to for an industry card: the groups it is shared with and whether auto-assign is on.
const renderIssueTo = (discountCard, t) => {
  const audienceGroups = Array.isArray(discountCard?.audience_restaurant_groups)
    ? discountCard.audience_restaurant_groups
    : [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      <ValueText sx={{ fontWeight: 600 }}>
        {t("discount-cards.index.discount-modal.issue-to-industry")}
      </ValueText>
      <ValueText>
        {t("discount-cards.index.discount-modal.issue-to-industry-description")}
      </ValueText>
      {audienceGroups.length > 0 ? (
        audienceGroups.map((group, index) => (
          <ValueText
            key={group?.id ?? index}
            data-testid="discount-card-global-details-audience-group-item"
          >
            {normalizeValue(group?.name)}
          </ValueText>
        ))
      ) : (
        <EmptyState>
          {t("discount-cards.index.discount-modal.issue-to-empty")}
        </EmptyState>
      )}
      <ValueText data-testid="discount-card-global-details-auto-assign" sx={{ mt: 0.5 }}>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {t("discount-cards.index.discount-modal.auto-assign")}
        </Box>{" "}
        {discountCard?.auto_assign_to_audience === true
          ? t("discount-cards.index.discount-modal.auto-assign-on")
          : t("discount-cards.index.discount-modal.auto-assign-off")}
      </ValueText>
    </Box>
  );
};

const renderExclusions = (discountCard, t) => {
  const groupedExclusions = groupExclusionItems(
    discountCard?.exclusion_items || [],
  );
  const hasFallbackExclusions = Boolean(discountCard?.exclusions);

  if (groupedExclusions.length === 0 && !hasFallbackExclusions) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {groupedExclusions.length > 0 ? (
        groupedExclusions.map((group) => (
          <ExclusionGroup key={group.category}>
            {group.category.toLowerCase() !== "general" && (
              <ExclusionCategory>{group.category}</ExclusionCategory>
            )}
            {group.items.map((item, itemIndex) => (
              <ExclusionItem key={`${group.category}-${itemIndex}-${item}`}>
                {item}
              </ExclusionItem>
            ))}
          </ExclusionGroup>
        ))
      ) : (
        <ExclusionItem style={{ whiteSpace: "pre-line" }}>
          {discountCard?.exclusions}
        </ExclusionItem>
      )}
    </Box>
  );
};

const DiscountCardGlobalDetailsView = ({ discountCard, restaurantGroupName }) => {
  const { t } = useTranslation();
  const hasBackgroundColor = Boolean(discountCard?.color);
  const hasScope = Boolean(discountCard?.restaurant_scope_type);
  const hasIssueTo = discountCard?.card_type === CARD_TYPE_INDUSTRY;
  const hasCategories =
    Array.isArray(discountCard?.rules) && discountCard.rules.length > 0;
  const hasContactMethods =
    normalizeItems(discountCard?.voice_contact_methods || []).length > 0 ||
    normalizeItems(discountCard?.message_contact_methods || []).length > 0;
  const hasExclusions =
    (discountCard?.exclusion_items || []).length > 0 ||
    Boolean(discountCard?.exclusions);

  if (
    !hasBackgroundColor &&
    !hasScope &&
    !hasIssueTo &&
    !hasCategories &&
    !hasContactMethods &&
    !hasExclusions
  ) {
    return null;
  }

  return (
    <GlobalDetailsContainer data-testid="discount-card-global-details-view">
      {hasScope && (
        <SectionCard data-testid="discount-card-global-details-restaurant-scope">
          <SectionHeader>
            <SectionHeaderTitle>
              {t("discount-cards.index.discount-modal.restaurant-usage")}
            </SectionHeaderTitle>
          </SectionHeader>
          <SectionBody>
            {renderRestaurantScope(discountCard, restaurantGroupName, t)}
          </SectionBody>
        </SectionCard>
      )}

      {hasIssueTo && (
        <SectionCard data-testid="discount-card-global-details-issue-to">
          <SectionHeader>
            <SectionHeaderTitle>
              {t("discount-cards.index.discount-modal.issue-to")}
            </SectionHeaderTitle>
          </SectionHeader>
          <SectionBody>{renderIssueTo(discountCard, t)}</SectionBody>
        </SectionCard>
      )}

      {hasBackgroundColor && (
        <SectionCard data-testid="discount-card-global-details-background-color">
          <SectionHeader>
            <SectionHeaderTitle>
              {t("discount-cards.index.discount-modal.background-color")}
            </SectionHeaderTitle>
          </SectionHeader>
          <SectionBody>
            <ColorSummary>
              <ColorSwatch
                data-testid="discount-card-global-details-background-color-swatch"
                sx={{ bgcolor: discountCard.color }}
              />
              <ValueText>{normalizeValue(discountCard.color)}</ValueText>
            </ColorSummary>
          </SectionBody>
        </SectionCard>
      )}

      {hasCategories && (
        <Box data-testid="discount-card-global-details-categories">
          <SectionTitle variant="h6">
            {t("discount-cards.create.form.rules.rules")}
          </SectionTitle>
          <Box sx={{ mt: 1 }}>{renderCategories(discountCard.rules, t)}</Box>
        </Box>
      )}

      {hasContactMethods && (
        <Box data-testid="discount-card-global-details-contact-methods">
          <SectionTitle variant="h6">
            {t("discount-cards.create.form.contact-methods.title")}
          </SectionTitle>
          <Box sx={{ mt: 1 }}>
            {renderContactMethods(
              {
                voice: discountCard.voice_contact_methods,
                message: discountCard.message_contact_methods,
              },
              t,
            )}
          </Box>
        </Box>
      )}

      {hasExclusions && (
        <Box data-testid="discount-card-global-details-exclusions">
          <SectionTitle variant="h6">
            {t("discount-cards.create.form.exclusions")}
          </SectionTitle>
          <SectionCard sx={{ mt: 1 }}>
            <SectionBody>{renderExclusions(discountCard, t)}</SectionBody>
          </SectionCard>
        </Box>
      )}
    </GlobalDetailsContainer>
  );
};

export default DiscountCardGlobalDetailsView;
